import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';
import get from 'lodash/get';

import { Message } from '@components';
import { useRelatedMinMaxValidation } from '@hooks';
import {
  DateTimeField,
  FieldWrapper,
  FormFieldset,
  HTMLField,
  ImageField,
  LocationsField,
  MultiSelectField,
  SelectField,
  FormError,
  TextField,
  TextAreaField,
  ToggleField,
} from '@Forms/fields.jsx';
import {
  removeNewlines,
  slugifyDash,
  urlFormat,
  slugifyOnBlur,
} from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

import './PostForm.scss';
import { DatamodelFormField } from '@Forms/DatamodelFormField/DatamodelFormField.jsx';

const PostForm = ({
  authorsData,
  categoriesData,
  disabled,
  mutation,
  onCancel,
  postData,
  siteData,
  siteID,
  relatedDatamodels,
  datamodelData,
}) => {
  const useFormReturn = useForm({
    defaultValues: postData,
    mode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
    control,
    reset,
    resetField,
    setFocus,
    setValue,
    trigger,
    watch,
  } = useFormReturn;
  const { isDirty, isValid } = useFormState({ control });

  // handling for tinymce fields
  const editorRefs = useRef({});

  const postType = watch('post_type');

  useEffect(() => {
    // set focus to the name field if this is a new post
    if (!postData.id) {
      setFocus('title');
    }
  }, [postData.id, setFocus]);

  const relatedModelListFields = useMemo(() => {
    return (datamodelData?.fields || [])
      .filter((f) => f.field_type === 'related_model_list')
      .map((f) => ({
        slug: f.slug,
        required: !!f.required,
        min_items: f.min_items,
        max_items: f.max_items,
      }));
  }, [datamodelData?.fields]);

  // validate min_items and max_items
  useRelatedMinMaxValidation({
    fields: relatedModelListFields,
    namePrefix: 'custom_fields',
    watch,
    errors,
    setError,
    clearErrors,
  });

  const postTypeChange = (e) => {
    if (!['external'].includes(e.target.value)) {
      // remove the external url if not an external post type
      setValue('external_url', null, { shouldDirty: true });
      clearErrors('external_url');
    }
  };

  const fieldsList = datamodelData?.fields?.map((field) => {
    const fieldName = `custom_fields.${field.slug}`;
    const fieldError = get(errors, fieldName);
    return (
      <div key={field.id} className={`sb-form-field-hideable`}>
        <DatamodelFormField
          datamodelField={field}
          fieldPrefix={`custom_fields.`}
          relatedDatamodels={relatedDatamodels}
          siteID={siteData?.id || null}
          useFormReturn={useFormReturn}
        />
        {field.field_type === 'related_model_list' && fieldError?.message && (
          <FormError message={fieldError.message} />
        )}
      </div>
    );
  });

  const onSubmit = (data) => {
    // update post data to format post.categories to expected format
    //
    // update the featured_image in the case it has been cleared in
    // the image field
    let categories = [];
    // add post type category if set
    if (data.post_type_category) {
      categories.push({ id: data.post_type_category });
    }
    // append the other assigned categories
    if (data.categories) {
      categories = [
        ...categories,
        ...data.categories.map((categoryId) => {
          return { id: categoryId };
        }),
      ];
    }
    data = {
      ...data,
      authors: data.authors
        ? data.authors.map((authorId) => {
            return { id: authorId };
          })
        : [],
      categories: categories,
      featured_image: data.featured_image.id ? data.featured_image : null,
    };
    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        // Only set field errors, skip __all__ errors as they are handled in the parent component
        errorsList
          .filter(({ field }) => field !== '__all__')
          .forEach(({ field, error }) => setError(field, error));
      },
    });
  };

  // list of options to pass to react-select
  let authorOptions = authorsData
    ? authorsData.objects.map((author) => {
        return { value: author.id, label: author.name };
      })
    : [];

  // list of options to pass to react-select
  let categoryOptions = categoriesData
    ? categoriesData.objects
        .filter((category) => !category.is_post_type)
        .map((category) => {
          return { value: category.id, label: category.name };
        })
    : [];
  // list of is_post_type categories
  let postCategories = categoriesData
    ? categoriesData.objects
        .filter((category) => category.is_post_type)
        .map((category) => {
          return (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          );
        })
    : [];

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.title}
          options={{ required: 'This field is required' }}
          name="title"
          placeholder="Blog post title..."
          className="sb-blog-post-title-field"
        />
        <HTMLField
          register={register}
          control={control}
          editorRefs={editorRefs}
          initialValue={postData.content}
          error={errors.content}
          name="content"
        />
        <TextAreaField
          register={register}
          error={errors.excerpt}
          options={{ onChange: removeNewlines }}
          name="excerpt"
          label="Post excerpt"
          helpText="Optional summary of what this post is about. Displayed on the posts list pages to pique interest for the reader. If not set, an excerpt will be automatically generated from the post content."
        />
      </FormFieldset>
      <FormFieldset legend="Categories">
        {postCategories.length > 0 && (
          <SelectField
            register={register}
            error={errors.post_type_category}
            name={`post_type_category`}
            options={{
              required: 'This field is required',
              valueAsNumber: true,
            }}
            label="Post type"
            helpText="Special post type used to distribute to specific blog sections"
          >
            <option value="">Select...</option>
            {postCategories}
          </SelectField>
        )}
        <MultiSelectField
          control={control}
          error={errors.categories}
          name={`categories`}
          label="Categories"
          options={categoryOptions}
          helpText="Assign one or more categories to this post."
        />
        {siteData?.business_id && siteData?.id && (
          <FieldWrapper
            error={errors.locations}
            label="Assign locations"
            name={'locations'}
            helpText="Requires location syndication enabled on blog."
          >
            <Controller
              control={control}
              name={'locations'}
              render={({ field, value, name, ref }) => (
                <LocationsField
                  businessID={siteData.business_id}
                  field={field}
                  defaultLocations={postData.locations || []}
                />
              )}
            />
          </FieldWrapper>
        )}
      </FormFieldset>
      <FormFieldset legend="Featured image">
        <ImageField
          useFormReturn={useFormReturn}
          label="Featured image"
          name="featured_image"
          siteID={siteID}
          helpText="Set a specific image to use as the primary post image. Used on the post list and displayed at the top of individual posts"
        />
        <TextField
          register={register}
          error={errors.featured_image_alt}
          name="featured_image_alt"
          label="Featured image alt"
          helpText="Set alternate text for the featured image."
        />
      </FormFieldset>
      {fieldsList?.length > 0 && (
        <FormFieldset legend="Custom fields">{fieldsList}</FormFieldset>
      )}
      <FormFieldset legend="Publishing options">
        <Controller
          control={control}
          name="date"
          id="date"
          render={({ field: controllerField }) => (
            <DateTimeField
              error={errors.date}
              field={controllerField}
              label="Published date"
              value={controllerField.value}
              helpText="Setting a date or time in the future will schedule the post."
            />
          )}
        />
        <ToggleField
          register={register}
          control={control}
          error={errors.published}
          name="published"
          label="Published"
          helpText="When unpublished, will be hidden from post list pages and will 404 if visited."
        />
      </FormFieldset>
      <FormFieldset legend="Authors">
        <MultiSelectField
          control={control}
          error={errors.authors}
          name={`authors`}
          label="Blog authors"
          options={authorOptions}
          helpText="Assign one or more blog authors to this post."
        />
      </FormFieldset>
      <FormFieldset legend="Special post type controls">
        <SelectField
          register={register}
          error={errors.post_type}
          options={{
            required: 'This field is required',
            onChange: postTypeChange,
          }}
          name="post_type"
          label="Change post type"
          helpText="Change the blog post type. External supports the External URL field to link outside of the blog"
        >
          <option value="post">Normal post</option>
          <option value="external">External URL/link</option>
        </SelectField>
        {postType == 'external' && (
          <TextField
            register={register}
            error={errors.external_url}
            options={{
              required: 'This field is required',
              onChange: urlFormat,
            }}
            name="external_url"
            label="External URL/link"
            helpText="Set the external URL for the post to link to."
          />
        )}
      </FormFieldset>
      {postType == 'post' && (
        <FormFieldset legend="SEO settings">
          <TextField
            register={register}
            options={{
              onChange: slugifyDash,
              onBlur: (e) => {
                slugifyOnBlur(e);
                setValue('slug', e.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              },
            }}
            error={errors.slug}
            name={`slug`}
            label="URL slug"
            helpText="Part of the blog post page URL"
          />

          <TextField
            register={register}
            error={errors.page_post_title}
            name={`page_post_title`}
            label="Page post title"
            helpText="Set a specific post title that will be used within the page title tag"
          />
          <TextAreaField
            register={register}
            error={errors.description}
            options={{ onChange: removeNewlines }}
            name="description"
            label="Meta description"
            rows={3}
            helpText="Page meta description for the post page. If not set, will automatically generate a description from the post content."
          />
          <TextAreaField
            register={register}
            error={errors.keywords}
            options={{ onChange: removeNewlines }}
            name="keywords"
            label="Meta keywords"
            rows={3}
            helpText="Page meta keywords for the post page"
          />
          <ToggleField
            register={register}
            control={control}
            error={errors.noindex}
            name="noindex"
            label="noindex"
            helpText="Set Post to Robots noindex for search engines."
          />
          <TextField
            register={register}
            error={errors.canonical}
            options={{ onChange: urlFormat }}
            name="canonical"
            label="Canonical URL"
            helpText="By default a canonical is set using the current blog post path. This field can be used to override it to point to different internal or external URL."
          />
        </FormFieldset>
      )}
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {onCancel && (
            <button
              className="sb-btn-cancel sb-btn"
              onClick={onCancel}
              disabled={disabled}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {mutation.isPending ? 'Publishing...' : 'Publish changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error publishing changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully published" />
        )}
      </div>
    </form>
  );
};

export default PostForm;
